import React, { createContext, useState, useEffect, useContext } from 'react';
import { authService } from '../apis/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const user = authService.getCurrentUser();
    if (user) {
      let fullName = (user.name || "").trim();
      try {
        const profileRaw = localStorage.getItem("profile");
        if (profileRaw) {
          const profile = JSON.parse(profileRaw);
          if (profile?.name?.trim()) {
            fullName = profile.name.trim();
            localStorage.setItem("username", fullName);
          }
        }
      } catch {
        // ignore
      }
      setCurrentUser({
        ...user,
        name: fullName || user.email?.split?.("@")?.[0] || "",
      });
    } else {
      setCurrentUser(null);
    }
    setLoading(false);
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (!currentUser) return;
    
    const refreshTokenInterval = setInterval(async () => {
      const result = await authService.refreshToken();
      if (!result.success) {
        // If refresh fails, log the user out
        logout();
      }
    }, 2 * 60 * 60 * 1000); // Refresh token every 14 minutes (before 15min expiry)
    
    return () => clearInterval(refreshTokenInterval);
  }, [currentUser]);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.login(email, password);
      if (result.success) {
        const fullName =
          result.data.name ||
          result.data.profile?.name ||
          email.split("@")[0];
        localStorage.setItem("username", fullName);
        setCurrentUser({
          email: result.data.email,
          name: fullName,
          isAdmin: result.data.isAdmin || false,
          role: result.data.role,
          userid: result.data.userid,
        });
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (name, email, password) => {
    setLoading(true);
    setError(null);
    try {
      const result = await authService.signup(name, email, password);
      if (result.success) {
        setCurrentUser({
          email: result.data.email,
          name: result.data.name
        });
        return { success: true };
      } else {
        setError(result.message);
        return { success: false, message: result.message };
      }
    } catch (err) {
      setError('An unexpected error occurred');
      return { success: false, message: 'An unexpected error occurred' };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    await authService.logout();
    setCurrentUser(null);
    localStorage.clear(); // Clear all data from localStorage
    setLoading(false);
  };

  const value = {
    currentUser,
    loading,
    error,
    login,
    signup,
    logout,
    isAuthenticated: !!currentUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};